import { Grid } from '@material-ui/core';
import { StandardDialog, Button } from '@roc/ui';
import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { TrackingNumber } from './trackingNumber';
import { Document } from '@roc/feature-types';
import { CustomTaskDialog } from '@roc/feature-general-tasks';
import { useLoanStore } from '@roc/feature-loans';
import { useStore } from '@roc/client-portal-shared/hooks';
import { LoanActionsService } from '../../../../loanActions/services/loanActionsService';

export type TrackingNumberDialogProps = {
  open?: boolean;
  onClose?: () => void;
  document: Document;
};

export const TrackingNumberDialog = observer(
  (props: TrackingNumberDialogProps) => {
    const { open, onClose, document } = props;
    const { loanStore } = useLoanStore();
    const { globalStore } = useStore();
    const loanActionsService = useMemo(() => new LoanActionsService(), []);
    const [trackingNumber, setTrackingNumber] = useState<string>('');
    const [submitting, setSubmitting] = useState<boolean>(false);

    useEffect(() => {
      if (open) {
        setTrackingNumber(
          loanStore.loanDetails?.loanClosingData?.fedexTrackingNumber ?? ''
        );
      }
    }, [open]);

    const onSubmit = async () => {
      setSubmitting(true);
      try {
        await loanActionsService.updateFedexTrackingNumber(
          loanStore.loanDetails?.loanId,
          trackingNumber
        );
        await loanStore.refreshLoanDetails();
        globalStore.notificationStore.showSuccessNotification({
          message: 'Tracking number updated successfully',
        });
        onClose && onClose();
      } catch (e) {
        globalStore.notificationStore.showErrorNotification({
          message: 'An error occurred while submitting the form.',
        });
      } finally {
        setSubmitting(false);
      }
    };

    const getContent = () => {
      return (
        <Grid container spacing={2}>
          <Grid item md={12}>
            <TrackingNumber
              value={trackingNumber}
              onChange={setTrackingNumber}
              disabled={submitting}
            />
          </Grid>
        </Grid>
      );
    };

    const getActions = () => {
      return (
        <Grid container spacing={1} justifyContent="flex-end">
          <Grid item>
            <Button
              onClick={onClose}
              color="primary"
              variant="outlined"
              testId="cancel"
            >
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              onClick={onSubmit}
              color="primary"
              variant="contained"
              testId="confirm"
              disabled={submitting}
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      );
    };

    return (
      <>
        {document?.taskId ? (
          <CustomTaskDialog
            open={open ?? false}
            title="Tracking Number"
            handleClose={onClose}
            dialogContent={getContent()}
            dialogActions={getActions()}
            taskId={document?.taskId}
          />
        ) : (
          <StandardDialog
            open={open ?? false}
            title="Tracking Number"
            maxWidth="sm"
            handleClose={onClose}
            dialogContent={getContent()}
            dialogActions={getActions()}
          />
        )}
      </>
    );
  }
);

export default TrackingNumberDialog;
